/**
 * One record per physical station from the unified DE/CH traffic-count dataset (DE: BASt Jawe;
 * CH: ASTRA SASVZ), collapsed server-side to one row per `(country, stationId)`, so the
 * frontend never dedups.
 *
 * Numeric class figures are `number | null` because a source can withhold a class — CH
 * stations publish only the combined light/heavy split — and a withheld value must read as
 * absent, never as `0`.
 */
import * as z from 'zod';

/**
 * An `R1`/`R2` source row, folded in at seed time. Directional rows carry the total and the
 * combined light/heavy split only; the single-class M1/N1 split exists on the total row alone.
 */
export interface TrafficStationDirection {
  /** Source direction code (`"R1"`/`"R2"`). */
  direction: string;
  /** e.g. `"Lübeck (N)"`. */
  directionName: string | null;
  /** Kfz/day. */
  dtvKfzGesamt: number | null;
  /** Cars + light commercial. */
  m1N1Kombiniert: number | null;
  /** Heavy commercial. */
  n2N3Kombiniert: number | null;
}

/**
 * `id` is the server row id, used as the marker key; `(country, stationId)` is the source
 * identity. `directions` is empty for a station whose source has only a total row.
 */
export interface TrafficStation {
  id: number;
  country: string;
  stationId: string;
  name: string;
  /** e.g. `"A1"`, `"H 1"`. */
  road: string | null;
  /** e.g. `"Bundesautobahn 1"`. */
  roadName: string | null;
  latitude: number;
  longitude: number;
  dataYear: number | null;
  /** Kfz/day, from the station's total row. */
  dtvKfzGesamt: number | null;
  /** Cars (M1). */
  m1Pkw: number | null;
  /** Light commercial (N1). */
  n1LeichteNfz: number | null;
  /** Heavy commercial (N2+N3). */
  n2N3Kombiniert: number | null;
  /** Cars + light commercial (M1+N1). */
  m1N1Kombiniert: number | null;
  directions: TrafficStationDirection[];
  /** Source attribution, e.g. `"BASt Jawe 2023"`. */
  quelle: string | null;
  hinweise: string | null;
}

/** Numeric fields rely on the server casting to numbers rather than `decimal` strings. */
export const trafficStationDirectionSchema = z.object({
  direction: z.string(),
  directionName: z.string().nullable(),
  dtvKfzGesamt: z.number().nullable(),
  m1N1Kombiniert: z.number().nullable(),
  n2N3Kombiniert: z.number().nullable(),
}) satisfies z.ZodType<TrafficStationDirection>;

/**
 * Parsed at the API boundary. The numeric figures require the server to cast coordinates and
 * DTV to `float`: Laravel's `decimal` emits JSON *strings* and would fail this parse.
 * Coordinates are non-nullable because the seeder drops coordinate-less stations.
 */
export const trafficStationSchema = z.object({
  id: z.number(),
  country: z.string(),
  stationId: z.string(),
  name: z.string(),
  road: z.string().nullable(),
  roadName: z.string().nullable(),
  latitude: z.number(),
  longitude: z.number(),
  dataYear: z.number().nullable(),
  dtvKfzGesamt: z.number().nullable(),
  m1Pkw: z.number().nullable(),
  n1LeichteNfz: z.number().nullable(),
  n2N3Kombiniert: z.number().nullable(),
  m1N1Kombiniert: z.number().nullable(),
  directions: z.array(trafficStationDirectionSchema),
  quelle: z.string().nullable(),
  hinweise: z.string().nullable(),
}) satisfies z.ZodType<TrafficStation>;

export const trafficStationsResultSchema = z.array(trafficStationSchema);
